'use client'

import Link from 'next/link'
import { useAuth } from '../contexts/AuthContext'

export default function FAQPage() {
  const { user } = useAuth()
  const faqs = [
    {
      category: 'General',
      questions: [
        {
          q: 'What is Deeliver?',
          a: 'Deeliver is a delivery coordination platform that connects Texas parents with verified drivers to deliver home-cooked meals to students at UT Austin. We make it easy and affordable for families to send meals directly to campus via in-person handoff.'
        },
        {
          q: 'How much does it cost?',
          a: 'Standard delivery fees start at $9 per order. Additional fees may apply for special dropoff locations. The exact cost is shown before you place your order.'
        },
        {
          q: 'Which campuses do you serve?',
          a: 'Currently, we serve UT Austin exclusively. We may expand to other campuses in the future.'
        }
      ]
    },
    {
      category: 'For Parents',
      questions: [
        {
          q: 'How do I place an order?',
          a: 'Create a parent account, select your pickup and dropoff locations, choose a driver, schedule your delivery date and time, and complete payment. You\'ll receive a confirmation and can view order status updates.'
        },
        {
          q: 'What are the packaging requirements?',
          a: 'Meals must be in a sealed, insulated bag labeled with your student\'s name, phone number, and the date the food was prepared. For perishable or time/temperature-controlled (TCS) foods, include a safe-handling statement on the label (e.g., "SAFE HANDLING: Keep refrigerated or in a cooler with ice packs. Consume within 4 hours of delivery."). Use insulated bags or coolers with ice packs to keep hot foods above 140\u00B0F or cold foods below 41\u00B0F during transit. See our example bag and label template in the order form for reference.'
        },
        {
          q: 'Can I track my delivery?',
          a: 'Yes! Once your order is picked up, you can view order status updates through the orders page. You\'ll also receive status updates as the delivery progresses.'
        },
        {
          q: 'What if my student doesn\'t receive the meal?',
          a: 'Contact support immediately if there\'s an issue. We work with drivers to resolve delivery problems and ensure your student receives their meal.'
        },
        {
          q: 'How do I pay?',
          a: 'We accept credit and debit cards through Stripe. Payment is required before the order is assigned to a driver.'
        }
      ]
    },
    {
      category: 'For Drivers',
      questions: [
        {
          q: 'How do I become a driver?',
          a: 'Register as a driver on our platform, provide your vehicle information, driver\'s license, and proof of insurance. You\'ll also need to connect your bank account to receive payments.'
        },
        {
          q: 'How much can I earn?',
          a: 'Drivers receive 70% of each delivery fee (70/30 split with the platform). Earnings depend on the number of orders you accept and complete. Most drivers earn between $50-200 per trip depending on capacity.'
        },
        {
          q: 'When do I get paid?',
          a: 'Payment is transferred to your connected bank account automatically after you mark an order as delivered and the student confirms receipt. Stripe handles the payout schedule (typically daily or weekly).'
        },
        {
          q: 'Do I need to connect my bank account?',
          a: 'Yes, connecting your bank account through Stripe is required before you can accept orders. This ensures secure, automatic payments for completed deliveries.'
        },
        {
          q: 'What routes are available?',
          a: 'We currently serve routes from Dallas-area pickup locations to UT Austin. Popular routes include Frisco, Plano, and Irving to various campus dropoff points.'
        }
      ]
    },
    {
      category: 'For Students',
      questions: [
        {
          q: 'How do I know when my meal is coming?',
          a: 'You\'ll receive notifications when your parent places an order, when the driver picks it up, when they\'re nearby, and when they arrive. You can also track the delivery in real-time.'
        },
        {
          q: 'How do I confirm delivery?',
          a: 'When the driver arrives, they\'ll provide you with a confirmation code. Enter this code in the "Confirm" section of your account to complete the delivery.'
        },
        {
          q: 'What if I\'m not available when the driver arrives?',
          a: 'Contact the driver directly using the contact information provided in your order details. Drivers typically have a delivery window, so coordinate a time that works for both of you.'
        },
        {
          q: 'Can I see my order history?',
          a: 'Yes, log in to your student account and navigate to the "Orders" tab to see all your past and current orders.'
        }
      ]
    },
    {
      category: 'Food Safety & Compliance',
      questions: [
        {
          q: 'Does Deeliver comply with Texas cottage food regulations?',
          a: 'Deeliver is a delivery coordination platform — we do not prepare or sell food. Parents prepare meals at home for their own students. However, we strongly encourage all families to follow Texas DSHS guidelines for safe food handling, including proper temperature control, labeling with the preparation date, and including a safe-handling statement for any perishable or TCS (time/temperature control for safety) foods.'
        },
        {
          q: 'What is a TCS food and how should I handle it?',
          a: 'TCS (Time/Temperature Control for Safety) foods include items like cooked rice, meat, dairy-based dishes, and cut fruits. These foods must be kept at safe temperatures — above 140\u00B0F for hot foods or below 41\u00B0F for cold foods. Use insulated containers with ice packs or heat packs, and ensure your student consumes the meal promptly after delivery.'
        },
        {
          q: 'What labeling is required on meals?',
          a: 'We require all meal packages to include: (1) the student\'s name and phone number, (2) the date the food was prepared, and (3) for any perishable/TCS items, a safe-handling statement such as "Keep refrigerated. Consume within 4 hours of delivery." This aligns with Texas DSHS labeling guidance for home-prepared foods.'
        },
        {
          q: 'How are meals kept at safe temperatures during delivery?',
          a: 'Parents are responsible for packaging meals in insulated bags or coolers with appropriate ice packs or heat packs. Drivers transport sealed packages as-is and do not open or modify packaging. We recommend deliveries be scheduled so food spends the minimum time in transit, and we provide packaging guidelines to help families keep meals in the safe temperature zone.'
        }
      ]
    },
    {
      category: 'Campus Delivery & Logistics',
      questions: [
        {
          q: 'How does delivery work on campus?',
          a: 'All deliveries are completed via direct, in-person handoff between the driver and the student. The driver meets the student at a designated campus location (such as outside Jester West or 26 West Apartments). We do not deliver to dorm front desks, mailrooms, or housing office reception areas — UT Housing only accepts packages from USPS and commercial shipping carriers at those locations.'
        },
        {
          q: 'What if I can\'t meet the driver at the scheduled time?',
          a: 'Contact your driver directly using the contact information in your order details to coordinate an alternate meetup time within their delivery window. If neither party can coordinate, the order will follow our standard missed-handoff policy (see our Terms of Service for details).'
        },
        {
          q: 'Where are the designated meetup spots?',
          a: 'Popular meetup locations include the area outside Jester West, 26 West Apartments lobby area, and other accessible outdoor or lobby spaces near campus housing. The exact meetup point is confirmed when the order is placed and both the student and driver can see it in their order details.'
        }
      ]
    },
    {
      category: 'Safety & Support',
      questions: [
        {
          q: 'Are drivers verified?',
          a: 'Yes. Every driver must submit a valid driver\'s license and proof of auto insurance before they can accept orders. We verify these documents manually. We do not currently run third-party background checks, but we are evaluating providers as we scale.'
        },
        {
          q: 'What if there\'s a problem with my order?',
          a: 'Contact our support team immediately. We\'re here to help resolve any issues with deliveries, payments, or driver interactions.'
        },
        {
          q: 'Is my payment information secure?',
          a: 'Yes, we use Stripe for all payment processing, which is PCI-compliant and uses industry-standard encryption to protect your financial information.'
        }
      ]
    }
  ]

  return (
    <div className="min-h-screen bg-cream text-ink">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 border-b border-ink/[0.06] bg-cream/85 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/" className="font-serif italic font-semibold text-2xl text-ink tracking-tight">
            Deeliver
          </Link>
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            <Link href="/about" className="text-ink-soft hover:text-ink transition text-sm font-medium flex items-center min-h-[44px]">
              About
            </Link>
            <Link href="/how-it-works" className="text-ink-soft hover:text-ink transition text-sm font-medium flex items-center min-h-[44px]">
              How it works
            </Link>
            <Link href="/faq" className="text-ink-soft hover:text-ink transition text-sm font-medium flex items-center min-h-[44px]">
              FAQ
            </Link>
            <Link href="/register?type=driver" className="text-ink-soft hover:text-ink transition text-sm font-medium flex items-center min-h-[44px]">
              Become a driver
            </Link>
            {user ? (
              <Link 
                href={
                  user.type === 'student' ? '/student/orders' :
                  user.type === 'parent' ? '/rider/new-order' :
                  user.type === 'driver' ? '/driver/schedule' :
                  user.type === 'admin' ? '/admin' : '/login'
                }
                className="text-ink-soft hover:text-ink transition text-sm font-medium flex items-center min-h-[44px]"
              >
                Dashboard
              </Link>
            ) : null}
            <Link 
              href="/rider/new-order" 
              className="bg-ember text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-sm font-semibold hover:bg-ember-hover transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-ember focus:ring-offset-2 focus:ring-offset-cream min-h-[44px] flex items-center justify-center"
            >
              Start an order
            </Link>
          </div>
          <div className="md:hidden">
            <Link 
              href="/rider/new-order" 
              className="bg-ember text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-ember-hover transition-colors"
            >
              Order
            </Link>
          </div>
        </div>
      </nav>
      <div className="max-w-4xl mx-auto px-8 py-16">
        <div className="mb-8">
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-ink mb-3">Frequently Asked Questions</h1>
          <p className="text-ink-soft">Find answers to common questions</p>
        </div>
        
        <div className="space-y-12">
          {faqs.map((category, categoryIndex) => (
            <section key={categoryIndex}>
              <h2 className="font-serif text-2xl font-semibold tracking-tight text-ink mb-6">{category.category}</h2>
              <div className="space-y-4">
                {category.questions.map((faq, index) => (
                  <div key={index} className="bg-white border border-cream-deep rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                    <h3 className="font-display text-lg font-semibold text-ink mb-2">{faq.q}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-12 bg-ember-soft/60 border border-ember/20 rounded-2xl p-6">
          <h3 className="font-display text-xl font-semibold text-ink mb-2">Still have questions?</h3>
          <p className="text-gray-700">
            If you can't find the answer you're looking for, please reach out to our support team. 
            We're here to help!
          </p>
        </div>

        <div className="mt-8">
          <Link 
            href="/"
            className="inline-block bg-ember text-white px-6 py-3 rounded-full font-semibold hover:bg-ember-hover transition-colors min-h-[44px]"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  )
}
